<template>
  <component
    :is="tag"
    class="contact-search">
    <slot
      :hits="hits"
      :facets="facets"
      :facet-items="facetItems"
      :total-count="totalCount"
      :total-page-count="totalPageCount"
      :loading="loading"
      :query="internalQuery"
      :refine="refine"
      :reload="reload"
      :fetch-all="fetchAll" />
  </component>
</template>

<script>
import SearchApi from '@/misc/apis/SearchApi'
import Vue from 'vue'

const emptyFacets = () => ({
  industries: [],
  ownerNames: [],
  preferredCommunicationChannels: [],
  tags: [],
  types: []
})

/**
 * Renderless data source for the contact search read model, replacing the
 * `<ais-instant-search index-name="contact">` + `<ais-configure>` combination.
 * Consumers read results through the default scoped slot.
 */
export default {
  name: 'ContactSearchProvider',
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    /** Defaults to the logged in account, which is what the endpoint ACL expects. */
    accountId: {
      type: [String, Number],
      default: null
    },
    /** Free-text search term. The provider also keeps its own copy so `refine()` works standalone. */
    query: {
      type: String,
      default: ''
    },
    /** Any subset of the ListContacts filter/sort fields. Unset fields mean "no filter". */
    filters: {
      type: Object,
      default: () => ({})
    },
    /** 1-based. 0 disables pagination and returns the full result set. */
    page: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    includeFacets: {
      type: Boolean,
      default: false
    },
    debounceDelay: {
      type: Number,
      default: 300
    },
    /** Skip searching entirely, for pickers rendered inside a collapsed or read-only section. */
    disabled: {
      type: Boolean,
      default: false
    },
    /** Run a first search on mount, matching InstantSearch's behaviour of pre-filling options. */
    searchOnMount: {
      type: Boolean,
      default: true
    },
    /** Suppress the error toast when the consumer reports failures itself. */
    silent: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hits: [],
      facets: emptyFacets(),
      totalCount: 0,
      totalPageCount: 0,
      loading: false,
      internalQuery: this.query,
      debounceTimeout: null,
      controller: null,
      requestId: 0
    }
  },
  computed: {
    resolvedAccountId() {
      return String(this.accountId || this.$store.getters['user/getAccountId'] || '')
    },
    /**
     * Facet buckets reshaped like the `ais-refinement-list` items the filter multiselects
     * were built against, so their labels and `track-by` bindings keep working.
     */
    facetItems() {
      return {
        types: this.toFacetItems(this.facets.types, this.filters.types),
        industries: this.toFacetItems(this.facets.industries, this.filters.industries),
        tags: this.toFacetItems(this.facets.tags, this.filters.tags),
        ownerNames: this.toFacetItems(this.facets.ownerNames, this.filters.ownerNames),
        preferredCommunicationChannels: this.toFacetItems(this.facets.preferredCommunicationChannels, this.filters.preferredCommunicationChannels)
      }
    },
    requestSignature() {
      return JSON.stringify({
        accountId: this.resolvedAccountId,
        filters: this.filters,
        page: this.page,
        itemsPerPage: this.itemsPerPage,
        includeFacets: this.includeFacets
      })
    }
  },
  watch: {
    query(newVal) {
      if (newVal !== this.internalQuery) {
        this.internalQuery = newVal
        this.scheduleSearch()
      }
    },
    requestSignature() {
      this.search()
    },
    disabled(newVal) {
      if (!newVal) {
        this.search()
      }
    }
  },
  mounted() {
    if (this.searchOnMount) {
      this.search()
    }
  },
  beforeDestroy() {
    clearTimeout(this.debounceTimeout)
    this.abortPending()
  },
  methods: {
    toFacetItems(buckets, refinedValues) {
      const refined = refinedValues || []
      return (buckets || []).map(bucket => ({
        label: bucket.value,
        value: bucket.value,
        count: bucket.count,
        isRefined: refined.includes(bucket.value)
      }))
    },
    abortPending() {
      if (this.controller) {
        this.controller.abort()
        this.controller = null
      }
    },
    buildRequest(overrides = {}) {
      return {
        accountId: this.resolvedAccountId,
        query: this.internalQuery,
        page: this.page,
        itemsPerPage: this.itemsPerPage,
        includeFacets: this.includeFacets,
        ...this.filters,
        ...overrides
      }
    },
    /** Sets the search term and triggers a debounced search. */
    refine(value) {
      const newQuery = value === null || value === undefined ? '' : String(value)
      if (newQuery === this.internalQuery) {
        return
      }
      this.internalQuery = newQuery
      this.$emit('update:query', newQuery)
      this.scheduleSearch()
    },
    scheduleSearch() {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(this.search, this.debounceDelay)
    },
    reload() {
      clearTimeout(this.debounceTimeout)
      return this.search()
    },
    search() {
      if (this.disabled || !this.resolvedAccountId) {
        return Promise.resolve()
      }
      this.abortPending()
      const controller = new AbortController()
      this.controller = controller
      const requestId = ++this.requestId
      this.loading = true

      return SearchApi.listContacts(this.buildRequest(), { signal: controller.signal })
        .then(response => {
          if (requestId !== this.requestId) {
            return
          }
          this.hits = response.hits
          this.totalCount = response.totalCount
          this.totalPageCount = response.totalPageCount
          if (this.includeFacets) {
            this.facets = response.facets
          }
          this.$emit('results', response)
        })
        .catch(e => {
          if (controller.signal.aborted || e?.code === 'ERR_CANCELED') {
            return
          }
          console.log(e)
          this.$emit('error', e)
          if (!this.silent) {
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          }
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false
            this.controller = null
          }
        })
    },
    /**
     * Fetches the full result set for the current filters, bypassing pagination.
     * Used by the address book "select all entries" action.
     */
    fetchAll() {
      if (!this.resolvedAccountId) {
        return Promise.resolve([])
      }
      return SearchApi.listContacts(this.buildRequest({ page: 0, itemsPerPage: 0, includeFacets: false }))
        .then(response => response.hits)
        .catch(e => {
          console.log(e)
          this.$emit('error', e)
          if (!this.silent) {
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          }
          return []
        })
    }
  }
}
</script>
